import { useEffect } from 'react';
import { BRAND, IS_PRODUCT } from '../brand';

/**
 * DocumentHead — sets <title>, <meta name="description">, and OG/Twitter
 * tags client-side for a route that doesn't control the <head> directly.
 *
 * Usage:
 *   <DocumentHead
 *     title="Page Title"
 *     description="Page description"
 *     ogImage="/previewlogo.png"
 *     ogType="website"
 *   />
 *
 * All params default to the build's brand (Utopian Creations unless
 * VITE_BRAND selects a product brand).
 */
const AGENCY_DEFAULTS = {
  title: 'Utopian Creations - Engineering the Future with Human Creativity and AI',
  description:
    'Innovative software development and AI integration services. We bring your digital dreams to life with cutting-edge technology and human creativity.',
  ogImage: 'https://utopiancreations.one/previewlogo.png',
  ogType: 'website',
  url: 'https://utopiancreations.one/',
  siteName: 'Utopian Creations',
  twitterCard: 'summary_large_image',
};

// Product mode (e.g. app.hollerpost.com) never falls back to agency copy or
// the agency OG image. No product OG image exists yet, so the card is a
// plain summary until one is designed.
const DEFAULTS = IS_PRODUCT
  ? {
      title: `${BRAND.name}: ${BRAND.tagline}`,
      description: `${BRAND.descriptor} ${BRAND.tagline}`,
      ogImage: '',
      ogType: 'website',
      url: `${BRAND.appOrigin}/`,
      siteName: BRAND.name,
      twitterCard: 'summary',
    }
  : AGENCY_DEFAULTS;

export default function DocumentHead({
  title,
  description,
  ogImage,
  ogType,
  url,
  siteName,
  twitterCard,
  robots,
  canonical,
  keywords,
  extra = [],
}) {
  const cfg = {
    title: title || DEFAULTS.title,
    description: description || DEFAULTS.description,
    ogImage: ogImage || DEFAULTS.ogImage,
    ogType: ogType || DEFAULTS.ogType,
    url: url || DEFAULTS.url,
    siteName: siteName || DEFAULTS.siteName,
    twitterCard: twitterCard || DEFAULTS.twitterCard,
    robots: robots || 'index, follow',
    canonical: canonical || DEFAULTS.url,
    keywords:
      keywords ||
      (IS_PRODUCT ? `${BRAND.name}, email marketing, SMS marketing, small business` : null) ||
      'software development, AI integration, web development, custom applications, digital innovation, artificial intelligence, React, Python, technology consulting',
  };

  useEffect(() => {
    document.title = cfg.title;

    const setMeta = (attrs) => {
      let selector = '';
      if (attrs.property) selector = `meta[property="${attrs.property}"]`;
      else if (attrs.name) selector = `meta[name="${attrs.name}"]`;
      else if (attrs['http-equiv']) selector = `meta[http-equiv="${attrs['http-equiv']}"]`;
      
      let el = selector ? document.querySelector(selector) : null;
      if (!el) {
        el = document.createElement('meta');
        if (attrs.property) el.setAttribute('property', attrs.property);
        else if (attrs.name) el.setAttribute('name', attrs.name);
        else if (attrs['http-equiv']) el.setAttribute('http-equiv', attrs['http-equiv']);
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };

    const setLink = (attrs) => {
      let selector = '';
      if (attrs.rel) selector = `link[rel="${attrs.rel}"]`;
      else selector = `link[${Object.entries(attrs).map(([k, v]) => `${k}="${v}"`).join('][')}]`;

      let el = selector ? document.querySelector(selector) : null;
      if (!el) {
        el = document.createElement('link');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };

    setMeta({ name: 'description', content: cfg.description });
    setMeta({ name: 'keywords', content: cfg.keywords });
    setMeta({ name: 'robots', content: cfg.robots });
    setMeta({ property: 'og:type', content: cfg.ogType });
    setMeta({ property: 'og:title', content: cfg.title });
    setMeta({ property: 'og:description', content: cfg.description });
    if (cfg.ogImage) setMeta({ property: 'og:image', content: cfg.ogImage });
    setMeta({ property: 'og:url', content: cfg.url });
    setMeta({ property: 'og:site_name', content: cfg.siteName });
    setMeta({ property: 'twitter:card', content: cfg.twitterCard });
    setMeta({ property: 'twitter:title', content: cfg.title });
    setMeta({ property: 'twitter:description', content: cfg.description });
    if (cfg.ogImage) setMeta({ property: 'twitter:image', content: cfg.ogImage });
    setLink({ rel: 'canonical', href: cfg.canonical });

    extra.forEach(({ tag, attrs }) => {
      if (tag === 'meta') setMeta(attrs);
      else if (tag === 'link') setLink(attrs);
    });
  }, [cfg.title, cfg.description, cfg.ogImage, cfg.ogType, cfg.url, cfg.siteName, cfg.twitterCard, cfg.robots, cfg.canonical, cfg.keywords, extra]);

  return null;
}
